import Fuse from "fuse.js";
import { useEffect, useState } from "react";
import { Option } from "~/lib/constants";
import { cn } from "~/lib/utils";

import { Checkbox } from "../checkbox";
import { Input } from "../input";
import { Label } from "../label";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { SpecialInput } from "./input";

export interface BaseProps {
  classNames?: string;
  placeholder?: string;
  searchPlaceholder?: string;
}

interface SpecialDropdownProps extends BaseProps {
  options: Option[];
  defaultValue: Option[];
  onChange?: (option: Option[]) => void;
  coverTriggerWidth?: boolean;
  emptyStateText?: string;
}
const fuseOptions = {
  isCaseSensitive: false,
  includeMatches: false,
  threshold: 0.3,
  keys: ["value", "label"],
};

export const SpecialMultiSelect = ({
  options,
  defaultValue,
  classNames,
  onChange,
  placeholder,
  searchPlaceholder,
  coverTriggerWidth,
  emptyStateText = "No data found",
}: SpecialDropdownProps) => {
  const [selectedOptions, setSelectedOptions] = useState(defaultValue);

  const handleCheckboxChange = (item: { label: string; value: string }) => {
    setSelectedOptions((prevSelected) => {
      const res = prevSelected?.some((opt) => opt.value === item.value)
        ? prevSelected?.filter((opt) => opt.value !== item.value)
        : [...prevSelected, item];
      if (onChange) {
        onChange(res);
      }
      return res;
    });
  };

  const fuse = new Fuse(options ?? [], fuseOptions);

  const [searchValue, setSearchValue] = useState("");

  const results = fuse?.search(searchValue);

  const resultLists = results?.map((result) => result.item);
  const filteredOptions =
    resultLists.length > 0 ? resultLists : searchValue ? resultLists : options;

  useEffect(() => {
    if (defaultValue && defaultValue.length > 0) {
      setSelectedOptions(defaultValue);
    }
  }, [defaultValue]);

  return (
    <div className="">
      <Popover>
        <PopoverTrigger className="w-full p-0 py-0">
          <SpecialInput
            placeholder={placeholder}
            classNames={classNames}
            selectedOptions={selectedOptions}
            onValueChange={handleCheckboxChange}
          />
        </PopoverTrigger>
        <PopoverContent
          style={{
            width: coverTriggerWidth
              ? "var(--radix-popover-trigger-width)"
              : "20rem",
          }}
          onWheel={(event) => event.stopPropagation()}
          align="start"
          className="h-fit w-fit space-y-2 overflow-auto rounded-2xl px-3 py-2"
        >
          {options?.length > 0 && (
            <Input
              value={searchValue}
              placeholder={searchPlaceholder || "Search"}
              prefixClass={"w-4 h-4 text-muted-foreground"}
              onChange={(event) => setSearchValue(event.target.value)}
            />
          )}

          <ul className="flex max-h-72 flex-col gap-0 overflow-y-auto">
            {filteredOptions?.length > 0 ? (
              filteredOptions.map((item, index) => (
                <li
                  onClick={() => handleCheckboxChange(item)}
                  key={index}
                  className={cn(
                    "group flex cursor-pointer items-center gap-2 bg-white px-4 py-2 hover:cursor-pointer hover:bg-neutral-300",
                    {
                      "bg-neutral-200": selectedOptions.some(
                        (opt) => opt.value === item.value,
                      ),
                    },
                  )}
                >
                  <Checkbox
                    className="h-4 w-4"
                    checkClassName="h-3 w-3"
                    onClick={(e) => e.stopPropagation()}
                    value={item.value}
                    checked={selectedOptions.some(
                      (opt) => opt.value === item.value,
                    )}
                    onCheckedChange={() => {
                      handleCheckboxChange(item);
                    }}
                  />
                  <Label className="text-sm">{item.label}</Label>
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-center text-sm text-neutral-500">
                {emptyStateText}
              </li>
            )}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
};
